import React, { useEffect, useState } from "react";
import "./LiveStreamPip.css";

// Live status comes from our own backend (server_py/app.py), proxied at /api.
// When live it returns { live:true, videoId, title, viewers, ... }; when offline
// it returns { live:false, past:[ { videoId, title, views, watchUrl, ... } ] }.
// The result is cached server-side so we never hammer the YouTube Data API quota.
const API = "/api/live/status";

// A perpetual YouTube live stream used only for ?livepreview=1 (so you can see
// and position the player without an actual eSmaaash live feed).
const PREVIEW_VIDEO_ID = "jfKfPfyJRdk";

function extractVideoId(url = "") {
  if (url.includes("embed/")) return url.split("embed/")[1].split("?")[0];
  if (url.includes("watch?v=")) return url.split("watch?v=")[1].split("&")[0];
  if (url.includes("youtu.be/")) return url.split("youtu.be/")[1].split("?")[0];
  return "";
}

// "12453" -> "12,453" (Indian grouping). Returns "" for empty/invalid.
function fmtCount(n) {
  const v = Number(n);
  if (!n || Number.isNaN(v)) return "";
  return v.toLocaleString("en-IN");
}

/**
 * Small floating player, docked bottom-left ("footer side").
 * - If the eSmaaash YouTube channel is LIVE, it shows the live stream with a
 *   "● LIVE" pill and the concurrent viewer count, and auto-closes after the
 *   countdown bar (pauses on hover).
 * - If NOT live, it shows the most recent PAST stream as a "REPLAY" with its
 *   total view count, and stays until the user closes it (no auto-dismiss).
 * ?livepreview=1 forces it visible with a demo stream for positioning.
 */
export default function LiveStreamPip() {
  const isPreview =
    typeof window !== "undefined" &&
    window.location.search.includes("livepreview");

  const [show, setShow] = useState(isPreview);
  const [videoId, setVideoId] = useState(isPreview ? PREVIEW_VIDEO_ID : "");
  const [title, setTitle] = useState("eSmaaash Live");
  const [viewers, setViewers] = useState(null); // concurrent (live) or total (past)
  const [isPast, setIsPast] = useState(false);
  const [open, setOpen] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [paused, setPaused] = useState(false); // pause auto-close while hovered

  useEffect(() => {
    if (isPreview) return; // preview forces it visible
    let cancelled = false;
    fetch(API)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data) return;

        if (data.live === true) {
          // New backend shape: { videoId }. Tolerate the legacy PHP shape
          // ({ videos: ["<url>"] }) so an old endpoint still works.
          const id =
            data.videoId ||
            (Array.isArray(data.videos) && data.videos.length
              ? extractVideoId(data.videos[0])
              : "");
          if (id) {
            setVideoId(id);
            setTitle(data.title || "eSmaaash Live");
            setViewers(data.viewers || null);
            setIsPast(false);
            setShow(true);
          }
          return;
        }

        // Not live → show the most recent past stream, if any.
        const past = Array.isArray(data.past) ? data.past : [];
        if (past.length) {
          const p = past[0];
          const id = p.videoId || extractVideoId(p.watchUrl || "");
          if (id) {
            setVideoId(id);
            setTitle(p.title || "eSmaaash");
            setViewers(p.views || null);
            setIsPast(true);
            setShow(true);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isPreview]);

  if (!show || !videoId || dismissed) return null;

  // Live autoplays muted; a replay waits for the user to press play.
  const embed = `https://www.youtube-nocookie.com/embed/${videoId}?${
    isPast ? "autoplay=0" : "autoplay=1&mute=1"
  }&controls=1&rel=0&playsinline=1`;

  const count = fmtCount(viewers);

  return (
    <div
      className={`live-pip${open ? "" : " collapsed"}${paused ? " paused" : ""}${
        isPast ? " is-past" : ""
      }`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="live-pip__bar">
        {isPast ? (
          <span className="live-pip__live live-pip__live--past">REPLAY</span>
        ) : (
          <span className="live-pip__live">
            <span className="live-pip__eq">
              <i />
              <i />
              <i />
            </span>
            LIVE
          </span>
        )}
        <span className="live-pip__title">{title || "eSmaaash Live"}</span>
        {count && (
          <span className="live-pip__count">
            👁 {count} {isPast ? "views" : "watching"}
          </span>
        )}
        <div className="live-pip__actions">
          <button
            type="button"
            className="live-pip__btn"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Minimize" : "Expand"}
          >
            {open ? "–" : "▢"}
          </button>
          <button
            type="button"
            className="live-pip__btn"
            onClick={() => setDismissed(true)}
            aria-label="Close player"
          >
            ✕
          </button>
        </div>
      </div>
      {open && (
        <div className="live-pip__video">
          <iframe
            src={embed}
            title={isPast ? "eSmaaash Past Stream" : "eSmaaash Live Stream"}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
      {/* Auto-close countdown — only for LIVE (a replay stays until closed). */}
      {!isPast && (
        <div className="live-pip__timer" aria-hidden="true">
          <i onAnimationEnd={() => setDismissed(true)} />
        </div>
      )}
    </div>
  );
}
